/**
 * ApiRequestLogService
 * Service for API request logging and monitoring
 * Route: api/apirequestlog
 */
import { BaseService } from '../base/BaseService';
/**
 * ApiRequestLogService
 * Handles API request log queries and analytics
 */
export class ApiRequestLogService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? ApiRequestLogService.DEFAULT_SERVICE_PATH,
            basePath: ApiRequestLogService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET API REQUEST LOGS ====================
    /**
     * Get API request logs with filtering
     * GET api/apirequestlog
     */
    async getApiRequestLogs(params) {
        const endpoint = this.getEndpoint('', '');
        const queryParams = {};
        if (params?.serviceName)
            queryParams.serviceName = params.serviceName;
        if (params?.httpMethod)
            queryParams.httpMethod = params.httpMethod;
        if (params?.path)
            queryParams.path = params.path;
        if (params?.minStatusCode !== undefined)
            queryParams.minStatusCode = params.minStatusCode.toString();
        if (params?.maxStatusCode !== undefined)
            queryParams.maxStatusCode = params.maxStatusCode.toString();
        if (params?.startDate)
            queryParams.startDate = params.startDate.toISOString();
        if (params?.endDate)
            queryParams.endDate = params.endDate.toISOString();
        if (params?.skip !== undefined)
            queryParams.skip = params.skip.toString();
        if (params?.take !== undefined)
            queryParams.take = params.take.toString();
        const url = this.buildUrlWithParams(endpoint, queryParams);
        const response = await this.http.get(url);
        return response.data ?? [];
    }
    // ==================== GET API REQUEST LOG BY ID ====================
    /**
     * Get API request log by ID
     * GET api/apirequestlog/{id}
     */
    async getApiRequestLog(id) {
        const endpoint = this.getEndpoint('by-id', `${id}`);
        const url = this.buildUrl(endpoint);
        const response = await this.http.get(url);
        return response.data ?? null;
    }
    // ==================== GET RECENT REQUESTS ====================
    /**
     * Get recent API requests
     * GET api/apirequestlog/recent
     */
    async getRecentRequests(count = 50, serviceName) {
        const endpoint = this.getEndpoint('recent', 'recent');
        const queryParams = { count: count.toString() };
        if (serviceName)
            queryParams.serviceName = serviceName;
        const url = this.buildUrlWithParams(endpoint, queryParams);
        const response = await this.http.get(url);
        return response.data ?? [];
    }
    // ==================== GET SLOW REQUESTS ====================
    /**
     * Get slow API requests
     * GET api/apirequestlog/slow
     */
    async getSlowRequests(thresholdMs = 1000, count = 100, serviceName) {
        const endpoint = this.getEndpoint('slow', 'slow');
        const queryParams = {
            thresholdMs: thresholdMs.toString(),
            count: count.toString(),
        };
        if (serviceName)
            queryParams.serviceName = serviceName;
        const url = this.buildUrlWithParams(endpoint, queryParams);
        const response = await this.http.get(url);
        return response.data ?? [];
    }
    // ==================== GET ERROR REQUESTS ====================
    /**
     * Get error API requests (status >= 400)
     * GET api/apirequestlog/errors
     */
    async getErrorRequests(count = 100, serviceName) {
        const endpoint = this.getEndpoint('errors', 'errors');
        const queryParams = { count: count.toString() };
        if (serviceName)
            queryParams.serviceName = serviceName;
        const url = this.buildUrlWithParams(endpoint, queryParams);
        const response = await this.http.get(url);
        return response.data ?? [];
    }
    // ==================== GET REQUEST SUMMARY ====================
    /**
     * Get API request summary statistics
     * GET api/apirequestlog/summary
     */
    async getRequestSummary(params) {
        const endpoint = this.getEndpoint('summary', 'summary');
        const queryParams = {};
        if (params?.serviceName)
            queryParams.serviceName = params.serviceName;
        if (params?.startDate)
            queryParams.startDate = params.startDate.toISOString();
        if (params?.endDate)
            queryParams.endDate = params.endDate.toISOString();
        const url = this.buildUrlWithParams(endpoint, queryParams);
        const response = await this.http.get(url);
        return response.data ?? null;
    }
    // ==================== GET BY CORRELATION ====================
    /**
     * Get API requests by correlation ID
     * GET api/apirequestlog/by-correlation/{correlationId}
     */
    async getRequestsByCorrelation(correlationId) {
        const endpoint = this.getEndpoint('by-correlation', `by-correlation/${correlationId}`);
        const url = this.buildUrl(endpoint);
        const response = await this.http.get(url);
        return response.data ?? [];
    }
    // ==================== GET STATS BY ENDPOINT ====================
    /**
     * Get statistics grouped by endpoint
     * GET api/apirequestlog/by-endpoint
     */
    async getStatsByEndpoint(serviceName, take = 50) {
        const endpoint = this.getEndpoint('by-endpoint', 'by-endpoint');
        const queryParams = { take: take.toString() };
        if (serviceName)
            queryParams.serviceName = serviceName;
        const url = this.buildUrlWithParams(endpoint, queryParams);
        const response = await this.http.get(url);
        return response.data ?? [];
    }
}
ApiRequestLogService.DEFAULT_SERVICE_PATH = 'system';
ApiRequestLogService.BASE_PATH = 'api/apirequestlog';
//# sourceMappingURL=ApiRequestLogService.js.map